import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import {
  ArrowLeft,
  Play,
  Trash2,
  FlaskConical,
  User,
  FileText,
  RotateCcw,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useSimulationCampaign,
  useSimulationEntries,
  useDeleteSimulationCampaign,
  useResetSimulationCampaign,
  type SimulationEntry,
} from '../api/simulations'
import { Pagination } from '../components/shared/Pagination'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { useStatusLabel } from '../i18n/statusLabels'
import { useDispositionLabel } from '../i18n/dispositionLabels'
import { formatDateTime, cn } from '../lib/utils'

const PAGE_SIZE = 20

const STATUS_TABS = [
  { labelKey: 'detail.statusTabs.all', value: undefined },
  { labelKey: 'detail.statusTabs.pending', value: 'pending' },
  { labelKey: 'detail.statusTabs.called', value: 'called' },
  { labelKey: 'detail.statusTabs.skipped', value: 'skipped' },
] as const

export function SimulationCampaignDetailPage() {
  const { t } = useTranslation('simulations')
  const { id } = useParams()
  const campaignId = id ? Number(id) : undefined
  const navigate = useNavigate()

  const { data: campaign, isLoading: campaignLoading, refetch } = useSimulationCampaign(campaignId)
  const deleteMutation = useDeleteSimulationCampaign()
  const resetMutation = useResetSimulationCampaign()

  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [offset, setOffset] = useState(0)

  const { data: entriesData, isLoading: entriesLoading, isError } = useSimulationEntries(
    campaignId,
    { limit: PAGE_SIZE, offset, status: statusFilter },
  )

  const handleDelete = () => {
    if (!campaign) return
    if (confirm(t('list.deleteConfirm', { name: campaign.name, count: campaign.total_entries }))) {
      deleteMutation.mutate(campaign.id, {
        onSuccess: () => navigate('/app/simulations'),
      })
    }
  }

  const handleReset = () => {
    if (!campaign || !campaign.stats || campaign.stats.contacted === 0) return
    if (confirm(t('detail.resetConfirm', { name: campaign.name, count: campaign.stats.contacted }))) {
      resetMutation.mutate(campaign.id)
    }
  }

  const handleStatusChange = (status: string | undefined) => {
    setStatusFilter(status)
    setOffset(0)
  }

  if (!campaignId) {
    navigate('/app/simulations')
    return null
  }

  const entries = entriesData?.entries ?? []
  const total = entriesData?.total ?? 0

  const stats = campaign?.stats
  const pct = stats && stats.total > 0 ? Math.round((stats.contacted / stats.total) * 100) : 0
  const isComplete = stats && stats.remaining === 0 && stats.total > 0

  return (
    <div className="space-y-5">
      {/* Back link */}
      <Link
        to="/app/simulations"
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('detail.back')}
      </Link>

      {/* Header */}
      {campaignLoading && <SpinLoader label={t('detail.loading')} />}
      {campaign && (
        <div className="rounded-lg border border-border bg-surface-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-info" />
                <h1 className="text-xl font-bold text-text-primary md:text-2xl">
                  {campaign.name}
                </h1>
              </div>
              <p className="mt-1 text-sm text-text-muted">
                {t('detail.entriesCount', { count: campaign.total_entries })}
                {campaign.description && ` · ${campaign.description}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/app/simulations/${campaignId}/run`)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
              >
                <Play className="h-4 w-4" />
                {t('detail.startRunning')}
              </button>
              <button
                onClick={handleReset}
                disabled={!stats || stats.contacted === 0 || resetMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:border-warning/50 hover:bg-warning/10 hover:text-warning disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-transparent disabled:hover:text-text-secondary"
                title={t('detail.resetTitle')}
              >
                <RotateCcw className="h-4 w-4" />
                {t('detail.reset')}
              </button>
              <button
                onClick={handleDelete}
                className="rounded-lg border border-border p-2.5 text-text-muted transition-colors hover:border-error/50 hover:bg-error/10 hover:text-error"
                title={t('list.deleteCampaign')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          {stats && (
            <div className="mt-4">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-elevated">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    isComplete ? 'bg-success' : 'bg-info',
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-text-muted">
                <span>{t('detail.runPct', { contacted: stats.contacted, total: stats.total, pct })}</span>
                <span>
                  {isComplete ? (
                    <span className="font-medium text-success">{t('list.complete')}</span>
                  ) : (
                    t('list.remaining', { count: stats.remaining })
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-1">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.labelKey}
            onClick={() => handleStatusChange(tab.value)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              statusFilter === tab.value
                ? 'bg-primary text-white'
                : 'bg-surface-elevated text-text-secondary hover:text-text-primary',
            )}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Loading / Error */}
      {entriesLoading && <SpinLoader label={t('detail.loadingEntries')} />}
      {isError && <ErrorCard message={t('detail.loadFailed')} onRetry={() => refetch()} />}

      {/* Empty state */}
      {!entriesLoading && entries.length === 0 && (
        <div className="rounded-lg border border-border bg-surface-card p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-text-muted" />
          <p className="mt-3 text-sm text-text-muted">
            {statusFilter
              ? t('detail.empty.noStatus', { status: statusFilter })
              : t('detail.empty.noEntries')}
          </p>
        </div>
      )}

      {/* Entries table (desktop) */}
      {entries.length > 0 && (
        <>
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-surface-card md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="px-4 py-3 w-12">{t('detail.table.index')}</th>
                  <th className="px-4 py-3">{t('detail.table.simulation')}</th>
                  <th className="px-4 py-3">{t('detail.table.personality')}</th>
                  <th className="px-4 py-3">{t('detail.table.scenario')}</th>
                  <th className="px-4 py-3">{t('detail.table.status')}</th>
                  <th className="px-4 py-3">{t('detail.table.disposition')}</th>
                  <th className="px-4 py-3">{t('detail.table.calledAt')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => (
                  <EntryRow key={entry.id} entry={entry} index={offset + i + 1} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Entries cards (mobile) */}
          <div className="space-y-2 md:hidden">
            {entries.map((entry, i) => (
              <EntryCard key={entry.id} entry={entry} index={offset + i + 1} />
            ))}
          </div>

          <Pagination offset={offset} limit={PAGE_SIZE} total={total} onPageChange={setOffset} />
        </>
      )}
    </div>
  )
}

function EntryRow({ entry, index }: { entry: SimulationEntry; index: number }) {
  const statusLabel = useStatusLabel(entry.status, 'simulationEntry')
  const dispositionLabel = useDispositionLabel(entry.disposition ?? '')
  return (
    <tr className="border-b border-border/50 transition-colors hover:bg-surface-elevated/50">
      <td className="px-4 py-3 text-text-muted">{index}</td>
      <td className="px-4 py-3 font-medium text-text-primary">
        {entry.simulation_name}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-text-secondary">
          <User className="h-3.5 w-3.5" />
          {entry.personality_name || '—'}
        </span>
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {entry.scenario_name || '—'}
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
            entry.status === 'called'
              ? 'bg-success/10 text-success'
              : entry.status === 'skipped'
                ? 'bg-text-muted/10 text-text-muted'
                : 'bg-info/10 text-info',
          )}
        >
          {statusLabel}
        </span>
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {entry.disposition ? dispositionLabel : <span className="text-text-muted">—</span>}
      </td>
      <td className="px-4 py-3 text-text-muted">
        {entry.called_at ? formatDateTime(entry.called_at) : '—'}
      </td>
    </tr>
  )
}

function EntryCard({ entry, index }: { entry: SimulationEntry; index: number }) {
  const { t } = useTranslation('simulations')
  const statusLabel = useStatusLabel(entry.status, 'simulationEntry')
  return (
    <div className="rounded-lg border border-border bg-surface-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">#{index}</span>
          <span className="font-medium text-text-primary">
            {entry.simulation_name}
          </span>
        </div>
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
            entry.status === 'called'
              ? 'bg-success/10 text-success'
              : entry.status === 'skipped'
                ? 'bg-text-muted/10 text-text-muted'
                : 'bg-info/10 text-info',
          )}
        >
          {statusLabel}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-3 text-xs text-text-secondary">
        <span className="inline-flex items-center gap-1">
          <User className="h-3 w-3" />
          {entry.personality_name}
        </span>
        <span>·</span>
        <span>{entry.scenario_name}</span>
      </div>
      {entry.description && (
        <p className="mt-1 text-xs text-text-muted line-clamp-2">
          {entry.description}
        </p>
      )}
      {entry.called_at && (
        <p className="mt-1 text-xs text-text-muted">
          {t('detail.card.calledLabel', { date: formatDateTime(entry.called_at) })}
        </p>
      )}
    </div>
  )
}
